import * as React from 'react'
import clsx from 'clsx'
import { ButtonBase, ButtonBaseProps } from '../ButtonBase'
import { SxProps, sx as sxFunc } from '../sx'
import { AccordionContext } from '../Accordion'
import style from './index.module.scss'
import ChevronRight from '../internal/svg-icons/ChevronRight'

export interface AccordionSummaryClasses {
  /** Styles applied to the root element. */
  root: string
  /** State class applied to the root element, children wrapper element and `IconButton` component if `expanded={true}`. */
  expanded: string
  /** State class applied to the ButtonBase root element if the button is keyboard focused. */
  focusVisible: string
  /** State class applied to the root element if `disabled={true}`. */
  disabled: string
  /** Styles applied to the root element unless `disableGutters={true}`. */
  gutters: string
  /** Styles applied to the children wrapper element unless `disableGutters={true}`. */
  contentGutters: string
  /** Styles applied to the children wrapper element. */
  content: string
  /** Styles applied to the `expandIcon`'s wrapper element. */
  expandIconWrapper: string
}

export interface AccordionSummaryProps extends ButtonBaseProps {
  /**
   * The content of the component.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<AccordionSummaryClasses>
  /**
   * The icon to display as the expand indicator.
   */
  expandIcon?: React.ReactNode
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
}

export const AccordionSummary = React.forwardRef<
  HTMLButtonElement,
  AccordionSummaryProps
>(function AccordionSummary(props, ref) {
  const {
    children,
    classes,
    className,
    expandIcon = (
      <ChevronRight fontSize={24} sx={{ textColor: 'neutral-400' }} />
    ),
    focusVisibleClassName,
    onClick,
    sx,
    ...other
  } = props

  const {
    disabled = false,
    disableGutters,
    expanded,
    toggle,
  } = React.useContext(AccordionContext)
  const handleChange = (event: React.SyntheticEvent) => {
    if (toggle) {
      toggle(event)
    }
    if (onClick) {
      onClick(event as any)
    }
  }

  return (
    <ButtonBase
      focusRipple={false}
      disableRipple
      disabled={disabled}
      Component="div"
      aria-expanded={expanded}
      className={clsx(
        classes?.root,
        style.root,
        disabled && [style.disabled, classes?.disabled],
        disableGutters ? style.disableGutters : classes?.gutters,
        expanded && classes?.expanded,
        className,
        sxFunc(sx)
      )}
      focusVisibleClassName={clsx(classes?.focusVisible, focusVisibleClassName)}
      onClick={handleChange}
      ref={ref}
      {...other}
    >
      <div
        className={clsx(
          style.content,
          !disableGutters && [style.enabledGutters, classes?.contentGutters],
          expanded && classes?.expanded,
          classes?.content
        )}
      >
        {children}
      </div>
      {expandIcon && (
        <div
          className={clsx(
            style.iconWrapper,
            classes?.expandIconWrapper,
            expanded && [style.expanded, classes?.expanded]
          )}
        >
          {expandIcon}
        </div>
      )}
    </ButtonBase>
  )
})
